import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

type Tier =
  | 'rank_1'
  | 'rank_2'
  | 'rank_3'
  | 'active_fan'
  | 'super_fan'
  | 'power_fan'
  | 'gift_master';

type Slot = 'thumbnail' | 'webm' | 'mov';

type TopFanAsset = {
  tier: Tier;
  label?: string;
  thumbnailUrl: string | null;
  webmUrl: string | null;
  movUrl: string | null;
  isActive: boolean;
  updatedAt?: string;
};

// Mirrors TOP_FAN_TIERS / TOP_FAN_TIER_LABELS in tezda-service-library.
// The first three are positional (the viewer's live leaderboard rank); the
// last four are threshold tiers the backend has not defined rules for yet —
// their assets upload fine and go live the moment the resolver supports them.
const TIERS: { key: Tier; label: string; note: string }[] = [
  { key: 'rank_1', label: '#1 Top Fan', note: 'Rank 1 on the session leaderboard' },
  { key: 'rank_2', label: '#2 Premium Fan', note: 'Rank 2 on the session leaderboard' },
  { key: 'rank_3', label: '#3 Elite Fan', note: 'Rank 3 on the session leaderboard' },
  { key: 'active_fan', label: 'Active Fan', note: 'Threshold tier — rules pending' },
  { key: 'super_fan', label: 'Super Fan', note: 'Threshold tier — rules pending' },
  { key: 'power_fan', label: 'Power Fan', note: 'Threshold tier — rules pending' },
  { key: 'gift_master', label: 'Gift Master', note: 'Threshold tier — rules pending' },
];

const SLOTS: {
  key: Slot;
  label: string;
  accept: string;
  contentType: (file: File) => string;
  urlField: keyof Pick<TopFanAsset, 'thumbnailUrl' | 'webmUrl' | 'movUrl'>;
  hint: string;
}[] = [
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    accept: '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp',
    contentType: (file) => (file.type && file.type.startsWith('image/') ? file.type : 'image/png'),
    urlField: 'thumbnailUrl',
    hint: 'Badge image shown in the entry banner',
  },
  {
    key: 'webm',
    label: 'WebM',
    accept: '.webm,video/webm',
    contentType: () => 'video/webm',
    urlField: 'webmUrl',
    hint: 'Android join animation',
  },
  {
    key: 'mov',
    label: 'MOV',
    accept: '.mov,video/quicktime',
    contentType: () => 'video/quicktime',
    urlField: 'movUrl',
    hint: 'iOS join animation',
  },
];

const TopFanAssetsPanel: React.FC = () => {
  const [assets, setAssets] = useState<Record<string, TopFanAsset>>({});
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const fetchAssets = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await fetch(`${walletApiBase}/getTopFanAssets`, { method: 'POST', headers, body: '{}' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load Top Fan assets');
      const map: Record<string, TopFanAsset> = {};
      (json.data || []).forEach((a: TopFanAsset) => {
        map[a.tier] = a;
      });
      setAssets(map);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [headerWithAuth]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleUpload = async (tier: Tier, slot: Slot, file: File) => {
    const headers = headerWithAuth();
    if (!headers) return;
    const slotConfig = SLOTS.find((s) => s.key === slot)!;
    const busy = `${tier}:${slot}`;
    setBusyKey(busy);
    try {
      const contentType = slotConfig.contentType(file);

      // Step 1: presign a deterministic key for this tier + slot, so a
      // re-upload replaces the file everyone already reads.
      const presignRes = await fetch(`${walletApiBase}/presignTopFanAssetUpload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Tier: tier, Slot: slot, ContentType: contentType }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const { FileKey, FileUrl, UploadUrl } = presignJson.data;

      // Step 2: upload straight to R2.
      const uploadRes = await fetch(UploadUrl, {
        method: 'PUT',
        body: await file.arrayBuffer(),
        headers: { 'Content-Type': contentType },
      });
      if (!uploadRes.ok) throw new Error('Failed to upload file to storage');

      // Step 3: register the URL so the live backend serves it on the next
      // join — Redis-backed, no redeploy.
      const saveRes = await fetch(`${walletApiBase}/saveTopFanAsset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Tier: tier, Slot: slot, FileKey, FileUrl }),
      });
      const saveJson = await saveRes.json();
      if (!saveRes.ok) throw new Error(saveJson.message || 'Failed to save asset');

      toast.success(`${slotConfig.label} updated for ${TIERS.find((t) => t.key === tier)?.label}`);
      setAssets((prev) => ({ ...prev, [tier]: saveJson.data }));
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setBusyKey('');
    }
  };

  const toggleActive = async (tier: Tier, nextActive: boolean) => {
    const headers = headerWithAuth();
    if (!headers) return;
    const busy = `${tier}:status`;
    setBusyKey(busy);
    try {
      const res = await fetch(`${walletApiBase}/setTopFanAssetStatus`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ Tier: tier, IsActive: nextActive }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update status');
      setAssets((prev) => ({ ...prev, [tier]: json.data }));
      toast.success(`${TIERS.find((t) => t.key === tier)?.label} ${nextActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update status');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div>
      <p className='text-xs text-gray-400 mb-6'>
        Rank/status assets for the Top Fan entry banner. When a Top Fan joins a livestream the backend sends the
        matching label, thumbnail and both animation formats on <span className='text-gray-300'>VIEWER_JOINED</span>{' '}
        and the join comment. WebM plays on Android, MOV on iOS, each falling back to the other format. An empty or
        deactivated slot is sent as null — never a wrong URL.
      </p>

      {loading ? (
        <div className='text-sm text-gray-400'>Loading Top Fan assets...</div>
      ) : (
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
          {TIERS.map((tier) => {
            const asset = assets[tier.key];
            const isActive = asset?.isActive !== false;
            const statusBusy = busyKey === `${tier.key}:status`;

            return (
              <div
                key={tier.key}
                className={`bg-white/5 backdrop-blur-sm border rounded-2xl overflow-hidden shadow-lg transition ${
                  isActive ? 'border-white/10' : 'border-white/10 opacity-60'
                }`}
              >
                {/* Preview: thumbnail over the WebM/MOV animation if present */}
                <div className='h-40 bg-black/20 flex items-center justify-center'>
                  {asset?.webmUrl || asset?.movUrl ? (
                    <video
                      src={asset.webmUrl || asset.movUrl || ''}
                      className='h-full w-full object-contain'
                      muted
                      loop
                      autoPlay
                      playsInline
                    />
                  ) : asset?.thumbnailUrl ? (
                    <img src={asset.thumbnailUrl} alt={tier.label} className='h-full w-full object-contain' />
                  ) : (
                    <span className='text-xs text-gray-500'>No assets uploaded</span>
                  )}
                </div>

                <div className='p-4 space-y-3'>
                  <div>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='text-sm font-semibold text-white'>{tier.label}</div>
                      <span
                        className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
                          isActive
                            ? 'border-green-400/40 text-green-300 bg-green-400/10'
                            : 'border-gray-500/40 text-gray-400 bg-gray-500/10'
                        }`}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className='text-[11px] text-gray-500 mt-0.5'>{tier.note}</div>
                  </div>

                  <div className='space-y-2'>
                    {SLOTS.map((slot) => {
                      const url = asset?.[slot.urlField] || null;
                      const isBusy = busyKey === `${tier.key}:${slot.key}`;
                      return (
                        <div key={slot.key} className='space-y-1'>
                          <div className='flex items-center justify-between gap-2'>
                            <span className='text-[11px] font-medium text-gray-300'>{slot.label}</span>
                            <span className={`text-[10px] ${url ? 'text-green-400' : 'text-gray-600'}`}>
                              {url ? 'set' : 'empty'}
                            </span>
                          </div>
                          {url && (
                            <a
                              href={url}
                              target='_blank'
                              rel='noreferrer'
                              className='block text-[10px] text-[#FF9C15] hover:underline break-all'
                            >
                              {url}
                            </a>
                          )}
                          <label
                            className={`block w-full text-center px-2 py-1.5 text-[11px] font-semibold rounded-lg cursor-pointer transition ${
                              isBusy
                                ? 'bg-white/10 text-gray-400 cursor-not-allowed'
                                : 'bg-white/10 text-white hover:bg-white/20'
                            }`}
                            title={slot.hint}
                          >
                            {isBusy ? 'Uploading...' : url ? `Replace ${slot.label}` : `Upload ${slot.label}`}
                            <input
                              type='file'
                              accept={slot.accept}
                              className='hidden'
                              disabled={isBusy}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) handleUpload(tier.key, slot.key, file);
                              }}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => toggleActive(tier.key, !isActive)}
                    disabled={statusBusy}
                    className={`w-full px-3 py-2 text-xs font-semibold rounded-lg transition disabled:opacity-50 ${
                      isActive
                        ? 'bg-white/10 text-gray-200 hover:bg-white/20'
                        : 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90'
                    }`}
                  >
                    {statusBusy ? 'Working...' : isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopFanAssetsPanel;
